/**
 * Componente de indicador de supervisión para estudiantes
 * Muestra cuando un instructor está observando la simulación
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Eye, MessageSquare, X, Bell, Lightbulb, AlertTriangle, UserCheck, Radio } from 'lucide-react';
import { toast } from 'sonner';
import { useSimulation } from '../context/SimulationContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export const useStudentSimulationWebSocket = (sessionId, enabled = true) => {
  const { setInstructorPaused, extendLocalTimeout, setVitals, setPatientState, markInstructorOverride, setActiveVitalsTransition } = useSimulation();
  const [isConnected, setIsConnected] = useState(false);
  const [supervisors, setSupervisors] = useState([]);
  const [hints, setHints] = useState([]);
  const [messages, setMessages] = useState([]);
  const [isBeingControlled, setIsBeingControlled] = useState(false);
  const [controllerName, setControllerName] = useState(null);
  const [lastSequenceNumber, setLastSequenceNumber] = useState(0);
  const lastSeqRef = useRef(0);
  // Keep ref in sync with state so `getWebSocketUrl` can read a stable value
  // without re-creating on every message (was causing an infinite disconnect
  // loop — 1006 abnormal-close every ~5s in production).
  useEffect(() => { lastSeqRef.current = lastSequenceNumber; }, [lastSequenceNumber]);
  
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);
  // v10.3.2 bug fix P0 en producción: deduplicación de eventos.
  // Antes, cada reconexión/replay/REST-poll re-emitía toasts fantasma
  // "El instructor modificó los signos vitales" incluso sin instructor.
  // Registramos los event_ids ya procesados y filtramos por actor_role.
  const processedEventIdsRef = useRef(new Set());
  const MAX_PROCESSED_IDS = 500;
  const rememberEventId = useCallback((id) => {
    if (!id) return false;
    const set = processedEventIdsRef.current;
    if (set.has(id)) return false;
    set.add(id);
    // Cap del set para evitar crecimiento indefinido en sesiones largas
    if (set.size > MAX_PROCESSED_IDS) {
      const excess = set.size - MAX_PROCESSED_IDS;
      let removed = 0;
      for (const v of set) {
        if (removed >= excess) break;
        set.delete(v);
        removed += 1;
      }
    }
    return true;
  }, []);

  const getWebSocketUrl = useCallback(() => {
    if (!sessionId) return null;
    const token = localStorage.getItem('token');
    if (!token) return null;

    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = BACKEND_URL.replace(/^https?:\/\//, '');
    // Read last_seq from a ref (not state) so this callback stays stable
    // across renders. Depending on `lastSequenceNumber` here created an
    // infinite disconnect/reconnect loop when messages updated the counter.
    return `${wsProtocol}//${wsHost}/api/ws/simulation/${sessionId}?token=${token}&mode=student&last_seq=${lastSeqRef.current || 0}`;
  }, [sessionId]);

  const sendVitalsUpdate = useCallback((vitals, patientState) => {
    if (wsRef.current?.readyState === WebSocket.OPEN && !isBeingControlled) {
      wsRef.current.send(JSON.stringify({
        type: 'vitals',
        vitals,
        patient_state: patientState
      }));
    }
  }, [isBeingControlled]);

  const sendActionPerformed = useCallback((actionType, details) => {
    if (wsRef.current?.readyState === WebSocket.OPEN && !isBeingControlled) {
      wsRef.current.send(JSON.stringify({
        type: 'action',
        action_type: actionType,
        details
      }));
    }
  }, [isBeingControlled]);

  const sendChatMessage = useCallback((content) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'chat',
        content
      }));
    }
  }, []);

  // v10.2.4: student asks the connected instructors to release control
  // (or to intervene). Fires a WS message that the backend fans out to
  // the instructors of the session as `control_requested`.
  const requestControl = useCallback((reason = '') => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'request_control',
        reason: reason || '',
      }));
      return true;
    }
    return false;
  }, []);

  const connect = useCallback(() => {
    if (!enabled) return;
    
    const url = getWebSocketUrl();
    if (!url) return;

    if (wsRef.current) {
      wsRef.current.close();
    }

    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('Student WebSocket connected');
      setIsConnected(true);
      
      heartbeatIntervalRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'heartbeat' }));
        }
      }, 30000);
    };

    ws.onclose = (event) => {
      console.log('Student WebSocket disconnected', event.code);
      setIsConnected(false);
      setSupervisors([]);
      
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
      
      // Reconectar si no fue cierre intencional
      if (event.code !== 1000 && enabled) {
        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, 5000);
      }
    };

    ws.onerror = (error) => {
      console.error('Student WebSocket error:', error);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        // Track sequence for replay on reconnect (best-effort). Compare
        // against the ref (not state) so this handler does NOT depend on
        // `lastSequenceNumber` — otherwise `connect` would churn identity
        // on every message and re-open the socket (1006 loop).
        if (typeof data.sequence_number === 'number' && data.sequence_number > lastSeqRef.current) {
          lastSeqRef.current = data.sequence_number;
          setLastSequenceNumber(data.sequence_number);
        }

        switch (data.event_type) {
          case 'connected':
            // Actualizar lista de supervisores
            if (data.session_info?.instructors) {
              setSupervisors(data.session_info.instructors);
            }
            // Full state_sync payload delivered on connect (bug fix P0):
            // hydrate local vitals/patient_state immediately so the student
            // monitor reflects any prior instructor changes.
            if (data.state) {
              if (data.state.vitals && Object.keys(data.state.vitals).length) {
                setVitals((prev) => ({ ...prev, ...data.state.vitals }));
              }
              if (data.state.patient_state) setPatientState(data.state.patient_state);
              if (data.state.is_paused) setInstructorPaused(true);
              if (typeof data.state.last_sequence_number === 'number') {
                setLastSequenceNumber(data.state.last_sequence_number);
              }
            }
            break;

          case 'state_sync':
            // Explicit re-sync response
            if (data.vitals) setVitals((prev) => ({ ...prev, ...data.vitals }));
            if (data.patient_state) setPatientState(data.patient_state);
            if (typeof data.last_sequence_number === 'number') {
              setLastSequenceNumber(data.last_sequence_number);
            }
            break;

          case 'replay':
            // Cross-pod recovery (RCA §48): after WS reconnect with ?last_seq=N,
            // the server sends the events we missed. Fold each event back into
            // local state as if it had arrived over the live channel.
            // v10.3.2: NO emitir toasts en replay — marcar todos los
            // event_ids como procesados para bloquear notificaciones
            // duplicadas si el WS live re-envía el mismo evento.
            (data.events || []).forEach((ev) => {
              rememberEventId(ev.event_id || ev.id);
              const t = ev.event_type;
              const p = ev.payload || {};
              const extras = ev; // top-level fields also present
              if (t === 'vitals_update' || t === 'vitals_modified_by_instructor' || t === 'clinical_event_activated' || t === 'action_performed') {
                if (markInstructorOverride) markInstructorOverride();
                const vs = extras.vitals || p.vitals || {};
                if (Object.keys(vs).length) setVitals((prev) => ({ ...prev, ...vs }));
                const ps = extras.patient_state || p.patient_state;
                if (ps) setPatientState(ps);
              } else if (t === 'patient_state_change') {
                const ps = extras.new_state || p.new_state || p.patient_state;
                if (ps) setPatientState(ps);
                const vs = extras.vitals || p.vitals || {};
                if (Object.keys(vs).length) setVitals((prev) => ({ ...prev, ...vs }));
              } else if (t === 'chat_message') {
                const role = extras.sender_role || (ev.actor && ev.actor.role);
                if (role === 'instructor') {
                  setMessages((prev) => [...prev, {
                    ...extras, ...p, sender_role: role,
                    sender_name: extras.sender_name || (ev.actor && ev.actor.user_name),
                  }]);
                }
              } else if (t === 'simulation_paused') {
                setInstructorPaused(true);
              } else if (t === 'simulation_resumed') {
                setInstructorPaused(false);
              }
              if (typeof ev.sequence_number === 'number') {
                setLastSequenceNumber((prev) => Math.max(prev, ev.sequence_number));
              }
            });
            break;

          case 'vitals_modified_by_instructor':
          case 'vitals_update':
            // v10.3.2 P0 en producción: gate estricto para el toast de
            // "instructor modificó los vitales". Antes se disparaba también
            // cuando el ALUMNO ejecutaba acciones REST (administer-medication)
            // que emitían `vitals_update` sin actor_role. Ahora exigimos:
            //   1) actor_role === 'instructor'  (o instructor_name presente)
            //   2) event_id no procesado previamente (dedup)
            //   3) evento en vivo (no replay de state_sync/replay/REST poll)
            const actorRole = data.actor_role || data.actor?.role;
            const fromInstructor =
              actorRole === 'instructor' || (!!data.instructor_name && !data.actor_role);
            const isLiveEvent = !data._replay && !data._hydration;
            const isNewEvent = rememberEventId(data.event_id || data.id);

            // 1) Aplicar cambios de estado SIEMPRE (el monitor debe reflejar
            //    el estado real independiente de quién lo generó).
            if (markInstructorOverride && fromInstructor) markInstructorOverride();
            if (data.transition && data.transition.from && data.vitals && setActiveVitalsTransition) {
              setActiveVitalsTransition({
                from: data.transition.from,
                target: data.vitals,
                duration_ms: data.transition.duration_ms || 5000,
                started_at: data.transition.started_at,
                appliedAtMs: performance.now(),
              });
              setVitals(data.vitals);
            } else {
              if (data.vitals) setVitals((prev) => ({ ...prev, ...data.vitals }));
              if (setActiveVitalsTransition) setActiveVitalsTransition(null);
            }
            if (data.patient_state) setPatientState(data.patient_state);

            // 2) Toast SÓLO si es instructor + evento nuevo + en vivo
            if (fromInstructor && isNewEvent && isLiveEvent) {
              toast.info(
                data.transition
                  ? `Transición de vitales (${Math.round((data.transition.duration_ms || 0) / 1000)}s)`
                  : 'El instructor modificó los signos vitales',
                {
                  icon: <Radio className="w-4 h-4" />,
                  duration: 4000,
                }
              );
            }
            break;

          case 'action_performed':
            // Bug fix P0 (v10.2.2): instructor's clinical actions (administer
            // medication, perform action, orders) may carry `effects` that
            // mutate vitals/patient_state. Backend now merges + broadcasts
            // the authoritative state top-level. Apply it here so the
            // student's monitor reflects the change without needing a
            // separate vitals_update event.
            if (data.vitals && Object.keys(data.vitals).length) {
              if (markInstructorOverride) markInstructorOverride();
              setVitals((prev) => ({ ...prev, ...data.vitals }));
            }
            if (data.patient_state) {
              if (markInstructorOverride) markInstructorOverride();
              setPatientState(data.patient_state);
            }
            if (data.action_type && data.user_id !== undefined) {
              // Only notify if the action came from someone else (instructor)
              // — avoid echo when the student is the actor.
              const label = data.action_type === 'administer_medication'
                ? `Instructor administró: ${data.details?.medication_name || 'medicamento'}`
                : `Instructor ejecutó acción: ${data.action_type}`;
              toast.info(label, {
                icon: <Radio className="w-4 h-4" />,
                duration: 5000,
              });
            }
            break;

          case 'patient_state_change':
            if (data.new_state) setPatientState(data.new_state);
            if (data.vitals) setVitals((prev) => ({ ...prev, ...data.vitals }));
            break;

          case 'clinical_event_activated':
            // Instructor triggered a clinical event that changes the scene.
            // Bug fix P0: use the AUTHORITATIVE top-level `vitals` +
            // `patient_state` fields (server-side merged state) instead of
            // the raw `effects` payload. Previously if effects only carried
            // partial deltas, the UI ignored the merged rest.
            if (markInstructorOverride) markInstructorOverride();
            if (data.vitals && Object.keys(data.vitals).length) {
              setVitals((prev) => ({ ...prev, ...data.vitals }));
            } else if (data.effects?.vitals) {
              setVitals((prev) => ({ ...prev, ...data.effects.vitals }));
            }
            if (data.patient_state) setPatientState(data.patient_state);
            else if (data.effects?.patient_state) setPatientState(data.effects.patient_state);
            toast.warning(`Evento clínico: ${data.event_name}`, {
              description: data.description || 'El instructor activó un evento en el escenario',
              icon: <AlertTriangle className="w-4 h-4" />,
              duration: 8000,
            });
            break;

          case 'simulation_ended':
          case 'simulation_ended_by_instructor':
            toast.warning('El instructor finalizó la simulación', {
              description: data.notes || 'Serás redirigido al debriefing',
              duration: 6000,
            });
            // Signal via context so SimulationPage can show debriefing.
            setInstructorPaused(false);
            break;

          case 'user_joined':
            if (data.is_instructor) {
              setSupervisors(prev => {
                // Evitar duplicados
                if (prev.some(s => s.user_id === data.user_id)) {
                  return prev;
                }
                return [...prev, {
                  user_id: data.user_id,
                  user_name: data.user_name,
                  connected_at: data.timestamp,
                  mode: data.mode || 'observador'
                }];
              });

              // Mostrar notificación persistente y más visible
              toast.info(
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Eye className="w-5 h-5 text-blue-500" />
                    <span className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  </div>
                  <div>
                    <p className="font-medium">Supervisión Activa</p>
                    <p className="text-sm text-gray-500">{data.user_name} está observando tu simulación</p>
                  </div>
                </div>,
                {
                  duration: 8000,
                  className: 'border-l-4 border-blue-500'
                }
              );
            }
            break;
          
          case 'user_left':
            // Solo mostrar notificación si era un instructor
            if (data.is_instructor) {
              const userName = data.user_name || 'Un supervisor';
              toast(
                <div className="flex items-center gap-2">
                  <Eye className="w-4 h-4 text-gray-400" />
                  <span>{userName} dejó de supervisar</span>
                </div>,
                { duration: 4000 }
              );
            }
            setSupervisors(prev => prev.filter(s => s.user_id !== data.user_id));
            // Bug fix P0 (v10.2.3): si el instructor que se desconecta
            // tenía el control, liberar el indicador naranja del alumno
            // — de lo contrario el card "Control del Instructor" se queda
            // pegado hasta refresh e impide interactuar con el header.
            if (data.had_control) {
              setIsBeingControlled(false);
              setControllerName(null);
            }
            break;
          
          case 'instructor_hint':
            const newHint = {
              id: Date.now(),
              instructor_name: data.instructor_name,
              hint_type: data.hint_type,
              content: data.content,
              timestamp: data.timestamp,
              read: false
            };
            setHints(prev => [...prev, newHint]);
            
            // Mostrar toast según tipo
            if (data.hint_type === 'alerta') {
              toast.warning(`Alerta de ${data.instructor_name}: ${data.content}`, {
                duration: 10000,
                icon: <AlertTriangle className="w-4 h-4" />
              });
            } else {
              toast.info(`Sugerencia de ${data.instructor_name}: ${data.content}`, {
                duration: 8000,
                icon: <Lightbulb className="w-4 h-4" />
              });
            }
            break;
          
          case 'chat_message':
            if (data.sender_role === 'instructor') {
              setMessages(prev => [...prev, data]);
              toast(`Mensaje de ${data.sender_name}`, {
                description: data.content,
                icon: <MessageSquare className="w-4 h-4" />
              });
            }
            break;
          
          case 'simulation_paused':
            setInstructorPaused(true);
            toast.warning(`Simulación pausada por ${data.paused_by || 'el instructor'}`, { duration: 8000 });
            break;
          
          case 'simulation_resumed':
            setInstructorPaused(false);
            toast.success(`Simulación reanudada${data.resumed_by ? ` por ${data.resumed_by}` : ''}`);
            break;
          
          case 'time_extended':
            if (data.added_minutes) {
              extendLocalTimeout(data.added_minutes * 60);
            }
            toast.info(`${data.extended_by || 'El instructor'} extendió el tiempo en ${data.added_minutes} minutos`, {
              duration: 8000
            });
            break;
          
          case 'mode_change':
            if (data.has_control) {
              setIsBeingControlled(true);
              setControllerName(data.instructor_name || 'Instructor');
              toast.warning('El instructor ha tomado el control de la simulación', {
                duration: 5000
              });
            }
            break;
          
          case 'control_released':
            setIsBeingControlled(false);
            setControllerName(null);
            toast.success('Has recuperado el control de la simulación');
            break;
          
          default:
            break;
        }
      } catch (e) {
        console.error('Error parsing WebSocket message:', e);
      }
    };
  }, [enabled, getWebSocketUrl, setInstructorPaused, extendLocalTimeout, setVitals, setPatientState, markInstructorOverride]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'Cierre intencional');
      wsRef.current = null;
    }
    setIsConnected(false);
    setSupervisors([]);
  }, []);

  const markHintAsRead = useCallback((hintId) => {
    setHints(prev => prev.map(h => 
      h.id === hintId ? { ...h, read: true } : h
    ));
  }, []);

  const clearHints = useCallback(() => {
    setHints([]);
  }, []);

  useEffect(() => {
    if (sessionId && enabled) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [sessionId, enabled, connect, disconnect]);

  // Cross-pod backplane (RCA §48): production runs replicas:2 with an
  // in-memory WS manager, so live broadcasts can drop when instructor and
  // student land on different pods. Poll the shared `simulation_events` log
  // via REST every 4 seconds and fold in any events we missed. WS is still
  // the primary channel; this is a safety net.
  useEffect(() => {
    if (!sessionId || !enabled) return undefined;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const url = `${BACKEND_URL}/api/supervision/session/${sessionId}/events?since=${lastSeqRef.current || 0}`;
        const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok) return;
        const d = await r.json();
        if (d.current_state) {
          const cs = d.current_state;
          if (cs.vitals && Object.keys(cs.vitals).length) {
            setVitals((prev) => ({ ...prev, ...cs.vitals }));
          }
          if (cs.patient_state) setPatientState(cs.patient_state);
          if (typeof cs.is_paused === 'boolean') setInstructorPaused(cs.is_paused);
        }
        (d.events || []).forEach((ev) => {
          const t = ev.event_type;
          const extras = ev;
          // v10.3.2: recordar event_id ANTES de aplicar. Si luego llega
          // el mismo evento por WS live, el gate del toast lo silencia
          // (evita doble notificación en escenarios con reconexión).
          rememberEventId(ev.event_id || ev.id);
          if (t === 'vitals_update' || t === 'vitals_modified_by_instructor' || t === 'clinical_event_activated' || t === 'action_performed') {
            if (markInstructorOverride) markInstructorOverride();
            const vs = extras.vitals || (ev.payload || {}).vitals || {};
            if (Object.keys(vs).length) setVitals((prev) => ({ ...prev, ...vs }));
            const ps = extras.patient_state || (ev.payload || {}).patient_state;
            if (ps) setPatientState(ps);
          } else if (t === 'chat_message') {
            const role = extras.sender_role || (ev.actor && ev.actor.role);
            if (role === 'instructor') {
              setMessages((prev) => {
                if (prev.some((m) => m.event_id === ev.event_id)) return prev;
                return [...prev, {
                  ...extras, ...(ev.payload || {}),
                  event_id: ev.event_id, sender_role: role,
                  sender_name: extras.sender_name || (ev.actor && ev.actor.user_name),
                }];
              });
            }
          } else if (t === 'simulation_paused') {
            setInstructorPaused(true);
          } else if (t === 'simulation_resumed') {
            setInstructorPaused(false);
          }
        });
        if (typeof d.last_sequence_number === 'number') {
          setLastSequenceNumber((prev) => Math.max(prev, d.last_sequence_number));
        }
      } catch (_e) {
        // Silent: never crash the sim on network hiccups.
      }
    };
    const iv = setInterval(tick, 4000);
    tick();
    return () => { cancelled = true; clearInterval(iv); };
  }, [sessionId, enabled, setVitals, setPatientState, setInstructorPaused]);

  return {
    isConnected,
    supervisors,
    hints,
    messages,
    isBeingControlled,
    controllerName,
    sendVitalsUpdate,
    sendActionPerformed,
    sendChatMessage,
    requestControl,
    markHintAsRead,
    clearHints,
    connect,
    disconnect
  };
};

// Componente visual de indicador de supervisión
export const SupervisionIndicator = ({ 
  supervisors, 
  hints, 
  isBeingControlled, 
  controllerName,
  onMarkHintRead,
  onClearHints 
}) => {
  const [showHints, setShowHints] = useState(false);
  const [showSupervisors, setShowSupervisors] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [controlCardDismissed, setControlCardDismissed] = useState(false);
  const unreadHints = hints.filter(h => !h.read);

  // Efecto de entrada cuando hay nuevos supervisores
  useEffect(() => {
    if (supervisors.length > 0) {
      setIsNew(true);
      const timer = setTimeout(() => setIsNew(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [supervisors.length]);

  // Bug fix P0 (v10.2.3): al empezar un nuevo take-control, resetear el
  // dismiss local para que el aviso sea visible otra vez.
  useEffect(() => {
    if (isBeingControlled) {
      setControlCardDismissed(false);
    }
  }, [isBeingControlled]);

  const showControlCard = isBeingControlled && !controlCardDismissed;

  if (supervisors.length === 0 && hints.length === 0 && !showControlCard) {
    return null;
  }

  return (
    <div className="fixed top-24 right-4 z-40 space-y-2 pointer-events-none" data-testid="supervision-indicator">
      {/* Control Warning - Instructor tiene el control */}
      {showControlCard && (
        <Card
          className="bg-orange-50 border-orange-300 shadow-lg pointer-events-auto"
          data-testid="instructor-control-card"
        >
          <CardContent className="p-3 flex items-center gap-2">
            <div className="p-1.5 bg-orange-100 rounded-full shrink-0">
              <Radio className="w-4 h-4 text-orange-600 animate-pulse" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-orange-800 truncate">Control del Instructor</p>
              <p className="text-[11px] text-orange-600 truncate">
                {controllerName || 'Instructor'} tiene el control
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 text-orange-700 hover:bg-orange-100 shrink-0"
              onClick={() => setControlCardDismissed(true)}
              data-testid="dismiss-instructor-control-card"
              title="Ocultar aviso"
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Supervisors Badge - Indicador principal */}
      {supervisors.length > 0 && !showControlCard && (
        <div className="relative pointer-events-auto">
          <Card 
            onClick={() => setShowSupervisors(!showSupervisors)}
            className={`cursor-pointer transition-all duration-300 shadow-md hover:shadow-lg ${
              isNew 
                ? 'bg-blue-100 border-blue-400 ring-2 ring-blue-300 ring-offset-2' 
                : 'bg-blue-50 border-blue-200 hover:bg-blue-100'
            }`}
          >
            <CardContent className="p-3 flex items-center gap-3">
              <div className="relative">
                <div className={`p-2 rounded-full ${isNew ? 'bg-blue-200' : 'bg-blue-100'}`}>
                  <Eye className="w-5 h-5 text-blue-600" />
                </div>
                {/* Indicador de "en vivo" */}
                <span className="absolute -top-1 -right-1 flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                </span>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-800">
                  {supervisors.length === 1 ? 'Supervisión Activa' : `${supervisors.length} Supervisores`}
                </p>
                <p className="text-xs text-blue-600">
                  {supervisors.length === 1 
                    ? supervisors[0].user_name 
                    : supervisors.map(s => s.user_name.split(' ')[0]).join(', ')
                  }
                </p>
              </div>
              <UserCheck className="w-4 h-4 text-blue-500" />
            </CardContent>
          </Card>

          {/* Lista de supervisores expandida */}
          {showSupervisors && supervisors.length > 1 && (
            <Card className="absolute top-full right-0 mt-2 w-64 shadow-lg z-50">
              <CardContent className="p-3">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-medium text-sm text-gray-700">Supervisores Conectados</h4>
                  <Button variant="ghost" size="sm" onClick={() => setShowSupervisors(false)} className="h-6 w-6 p-0">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                <div className="space-y-2">
                  {supervisors.map((supervisor) => (
                    <div 
                      key={supervisor.user_id}
                      className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg"
                    >
                      <div className="relative">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                          <span className="text-sm font-medium text-blue-700">
                            {supervisor.user_name?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <span className="absolute bottom-0 right-0 w-2 h-2 bg-green-500 rounded-full border border-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-700 truncate">{supervisor.user_name}</p>
                        <p className="text-xs text-gray-500">
                          {supervisor.mode === 'control_total' ? 'Control Total' : 
                           supervisor.mode === 'asistido' ? 'Modo Asistido' : 'Observando'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Hints Panel */}
      {hints.length > 0 && (
        <div className="relative pointer-events-auto">
          <Button
            variant="outline"
            size="sm"
            className={`flex items-center gap-2 ${unreadHints.length > 0 ? 'bg-yellow-50 border-yellow-300 text-yellow-700' : ''}`}
            onClick={() => setShowHints(!showHints)}
          >
            <Lightbulb className="w-4 h-4" />
            <span>Pistas ({hints.length})</span>
            {unreadHints.length > 0 && (
              <span className="ml-1 w-5 h-5 rounded-full bg-yellow-500 text-white text-xs flex items-center justify-center">
                {unreadHints.length}
              </span>
            )}
          </Button>

          {showHints && (
            <Card className="absolute top-full right-0 mt-2 w-80 shadow-lg z-50">
              <CardContent className="p-3">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-medium text-sm">Pistas del Instructor</h4>
                  <div className="flex gap-1">
                    {hints.length > 0 && (
                      <Button variant="ghost" size="sm" onClick={onClearHints} className="text-xs h-6 px-2">
                        Limpiar
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => setShowHints(false)} className="h-6 w-6 p-0">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {hints.map((hint) => (
                    <div
                      key={hint.id}
                      onClick={() => onMarkHintRead(hint.id)}
                      className={`p-2 rounded-lg text-sm cursor-pointer transition-colors ${
                        hint.read 
                          ? 'bg-gray-50' 
                          : hint.hint_type === 'alerta' 
                            ? 'bg-red-50 border-l-4 border-red-500' 
                            : 'bg-yellow-50 border-l-4 border-yellow-500'
                      }`}
                    >
                      <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                        {hint.hint_type === 'alerta' ? (
                          <AlertTriangle className="w-3 h-3 text-red-500" />
                        ) : (
                          <Lightbulb className="w-3 h-3 text-yellow-500" />
                        )}
                        <span>{hint.instructor_name}</span>
                        <span>•</span>
                        <span>{new Date(hint.timestamp).toLocaleTimeString()}</span>
                      </div>
                      <p className="text-gray-700">{hint.content}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default SupervisionIndicator;
